import { Typography, type TypographyProps } from '@mui/material';
import { useMemo } from 'react';

const TelephoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;

const isTelephoneString = (value: string): boolean => {
  return !!value.trim().replaceAll(' ', '').match(TelephoneRegex)?.length;
};

type LinkType = 'same-domain-url' | 'external-url' | 'mailto' | 'tel' | 'text';

const getCorrectLink = (
  inputValue: string,
  linkType: LinkType = 'text'
): {
  linkValue: string;
  type: LinkType;
} => {
  if (!inputValue) {
    return { linkValue: '', type: 'text' };
  }
  let value = inputValue.toLowerCase().trim();

  // link scenarios
  if (value.startsWith('http') || value.startsWith('www') || ['same-domain-url', 'external-url'].includes(linkType)) {
    try {
      const url = new URL(value);
      if (url.host === window.location.host) {
        return {
          linkValue: value,
          type: 'same-domain-url',
        };
      }
    } catch (e) {}

    if ((value.startsWith('https') && !value.startsWith('https')) || value.startsWith('www')) {
      value = `https://${value}`;
    }
    return { linkValue: value, type: 'external-url' };
  }

  // mailto scenarios
  if (value.startsWith('mailto') || value.match(/@/) || linkType === 'mailto') {
    if (!value.startsWith('mailto')) {
      value = `mailto:${value}`;
    }

    return { linkValue: value, type: 'mailto' };
  }

  // tel scenarios
  if (value.startsWith('tel') || isTelephoneString(value) || linkType === 'tel') {
    if (!value.startsWith('tel')) {
      value = `tel:${value}`;
    }

    return { linkValue: value, type: 'tel' };
  }

  // default text
  return { linkValue: value, type: 'text' };
};

type Props = {
  href?: string | null;
  title?: string;
  linkType?: LinkType;
} & TypographyProps;

/**
 * UiText - A component to render text with different link types
 * It can infer the link type from the href or linkType prop
 */
const UiText = ({ href, title, linkType, ...typographyProps }: Props) => {
  const { type, linkValue } = useMemo(() => {
    return getCorrectLink(href ?? '', linkType);
  }, [href, linkType]);

  return (
    <Typography
      {...typographyProps}
      {...{
        ...(type === 'same-domain-url' && { component: 'a', href: new URL(linkValue)?.pathname }),
        ...(type === 'external-url' && { component: 'a', href: linkValue, target: '_blank', rel: 'noreferrer' }),
        ...(['mailto', 'tel'].includes(type) && { component: 'a', href: linkValue }),
      }}
    >
      {title ?? ''}
    </Typography>
  );
};

export default UiText;
